import { useEffect, useId, useMemo, useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';

/**
 * Edicoes de celula em andamento. Um campo/celula com texto digitado que ainda NAO subiu para o estado da tela (a celula so
 * confirma no blur, Enter ou Tab) registra aqui uma funcao de confirmacao. Salvar (botao da barra ou ⌘S) chama
 * `confirmarEdicoes()` ANTES de gravar: assim digitar e apertar ⌘S direto salva o valor novo, nao o anterior. Enquanto ha
 * edicao registrada, a barra considera o registro alterado (o botao Salvar nao fica desabilitado por causa do foco na celula).
 */
type Confirmar = () => void;

const ativas = new Map<string, Confirmar>();
const ouvintes = new Set<() => void>();
let versao = 0;

function avisar(): void {
  versao += 1;
  for (const o of [...ouvintes]) o();
}

/** Registra (ou troca) a confirmacao da edicao `chave`. Devolve a funcao que a remove. */
export function registrarEdicao(chave: string, confirmar: Confirmar): () => void {
  ativas.set(chave, confirmar);
  avisar();
  return () => removerEdicao(chave);
}

export function removerEdicao(chave: string): void {
  if (ativas.delete(chave)) avisar();
}

export function haEdicaoPendente(): boolean {
  return ativas.size > 0;
}

/**
 * Confirma todas as edicoes em andamento e deixa o React aplicar os estados novos antes de voltar (flushSync): quem chama
 * ja encontra a tela renderizada com os valores confirmados.
 */
export function confirmarEdicoes(): void {
  if (ativas.size === 0) return;
  flushSync(() => {
    for (const confirmar of [...ativas.values()]) confirmar();
  });
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/** true enquanto alguma celula tem edicao nao confirmada. */
export function useEdicaoPendente(): boolean {
  useSyncExternalStore(assinar, () => versao);
  return haEdicaoPendente();
}

/** Ganchos de edicao de celula para o AG Grid: registra a confirmacao (stopEditing) enquanto uma celula esta em edicao. */
export function useEdicaoDeGrade(): {
  onCellEditingStarted: (e: { api: { stopEditing: (cancelar?: boolean) => void } }) => void;
  onCellEditingStopped: () => void;
} {
  const id = useId();
  useEffect(() => () => removerEdicao(id), [id]);
  return useMemo(
    () => ({
      onCellEditingStarted: (e) => {
        registrarEdicao(id, () => e.api.stopEditing(false));
      },
      onCellEditingStopped: () => removerEdicao(id),
    }),
    [id],
  );
}
